import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuote } from "@/context/QuoteContext";
import { REVIEWS, REVIEW_STATS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_SEARCH_URL = "https://www.google.com/search?q=Templar+Armor+Roofing+%26+Construction+reviews";
const AVATAR_COLORS = ["bg-red-600", "bg-blue-600", "bg-green-600", "bg-purple-600", "bg-amber-600", "bg-teal-600"];

const GoogleG = ({ className = "h-6 w-6" }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001 6.19 5.238 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
  </svg>
);

const Stars = ({ size = "h-4 w-4", n = 5 }) => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`${size} ${i < n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
    ))}
  </div>
);

export const GoogleReviews = () => {
  const { openQuote } = useQuote();
  const [data, setData] = useState({
    rating: REVIEW_STATS.rating,
    count: REVIEW_STATS.count,
    url: GOOGLE_SEARCH_URL,
    reviews: REVIEWS.map((r) => ({ name: r.name, photo: "", rating: 5, date: r.date, text: r.text })),
  });

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/google-reviews`)
      .then((res) => {
        const d = res.data;
        if (active && d && d.configured && Array.isArray(d.reviews) && d.reviews.length > 0) {
          setData({
            rating: d.rating || REVIEW_STATS.rating,
            count: d.count || REVIEW_STATS.count,
            url: d.url || GOOGLE_SEARCH_URL,
            reviews: d.reviews,
          });
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  return (
    <section id="reviews" data-testid="reviews-section" className="py-16 sm:py-20 lg:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <div className="flex items-center gap-5">
            <GoogleG className="h-14 w-14" />
            <div>
              <div className="flex items-center gap-3">
                <span data-testid="reviews-rating" className="font-display font-extrabold text-4xl text-slate-950">
                  {Number(data.rating).toFixed(1)}
                </span>
                <div>
                  <Stars size="h-5 w-5" n={Math.round(data.rating)} />
                  <p className="text-sm text-slate-500 font-medium mt-0.5">{data.count}+ Google reviews</p>
                </div>
              </div>
              <p className="mt-1 font-display font-bold text-slate-800">Rated Excellent by California Homeowners</p>
            </div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.reviews.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, delay: (i % 3) * 0.1 }}
              data-testid={`review-${i}`}
              className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {r.photo ? (
                    <img src={r.photo} alt={r.name} className="h-11 w-11 rounded-full object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <div className={`grid place-items-center h-11 w-11 rounded-full ${AVATAR_COLORS[i % AVATAR_COLORS.length]} text-white font-display font-bold text-lg`}>
                      {(r.name || "G").charAt(0)}
                    </div>
                  )}
                  <div>
                    <div className="font-display font-bold text-slate-900">{r.name}</div>
                    <div className="text-xs text-slate-400">{r.date}</div>
                  </div>
                </div>
                <GoogleG className="h-6 w-6" />
              </div>
              <div className="mt-4"><Stars n={r.rating || 5} /></div>
              <p className="mt-3 text-slate-600 leading-relaxed">{r.text}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="font-display font-bold text-xl text-slate-900">Ready to join hundreds of happy California homeowners?</p>
          <Button
            data-testid="reviews-quote-btn"
            onClick={() => openQuote()}
            size="lg"
            className="mt-5 bg-red-700 hover:bg-red-800 text-white font-bold rounded-full h-14 px-8 hover:scale-105 transition-transform"
          >
            Get My Free Quote <ArrowRight className="ml-1 h-5 w-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};
